import React, { useEffect, useState } from "react";
import { CheckCircle2, MessageCircle, ShieldCheck } from "lucide-react";
import api, { formatApiError } from "@/lib/api";

export default function WhatsAppVerification({ phone, purpose = "registration", onVerified }) {
  const [optIn, setOptIn] = useState(false);
  const [code, setCode] = useState("");
  const [sent, setSent] = useState(false);
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    setCode(""); setSent(false); setVerified(false); setMessage("");
    onVerified?.("");
  }, [phone, purpose]); // eslint-disable-line react-hooks/exhaustive-deps

  const base = `/api/whatsapp/otp/${purpose}/`;

  const requestCode = async () => {
    setBusy(true); setMessage("");
    try {
      const { data } = await api.post(base + "request", { phone, opt_in: optIn });
      setSent(true);
      setMessage(`Código enviado a ${data.phone_masked}. Escríbelo abajo. Vence en pocos minutos.`);
    } catch (e) {
      setSent(false);
      setMessage(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const confirmCode = async () => {
    setBusy(true); setMessage("");
    try {
      const { data } = await api.post(base + "confirm", { phone, code });
      setVerified(true);
      onVerified?.(data.verification_token);
      setMessage("Número de WhatsApp verificado.");
    } catch (e) {
      setMessage(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rounded-xl border p-4 space-y-3"
      style={{
        borderColor: verified ? "rgba(31,167,116,.45)" : "var(--kd9-border)",
        background: "rgba(31,167,116,.04)",
      }}
      data-testid="whatsapp-verification"
    >
      <div className="flex items-center gap-2 font-mont text-sm font-semibold">
        {verified ? <CheckCircle2 size={17} color="#1FA774" /> : <ShieldCheck size={17} color="var(--kd9-gold)" />}
        WhatsApp Seguro KD9
      </div>

      {!verified && (
        <>
          <label className="flex items-start gap-2 text-xs font-mont" style={{ color: "var(--kd9-text-soft)" }}>
            <input
              type="checkbox"
              checked={optIn}
              onChange={(e) => setOptIn(e.target.checked)}
              className="mt-0.5"
            />
            Autorizo verificar este número y recibir únicamente notificaciones importantes de mi cuenta.
          </label>

          <button
            type="button"
            disabled={busy || !phone || !optIn}
            onClick={requestCode}
            className="kd9-btn-outline w-full py-2 rounded-lg text-[11px] flex justify-center items-center gap-2"
          >
            <MessageCircle size={14} />
            {busy ? "Enviando…" : sent ? "Reenviar código por WhatsApp" : "Enviar código por WhatsApp"}
          </button>

          <div className="space-y-2 pt-1">
            <label
              htmlFor="whatsapp-otp-code"
              className="block text-[11px] font-mont uppercase tracking-wider"
              style={{ color: "var(--kd9-text-soft)" }}
            >
              Código de verificación
            </label>
            <div
              data-testid="whatsapp-otp-row"
              style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}
            >
              <input
                id="whatsapp-otp-code"
                type="tel"
                inputMode="numeric"
                pattern="[0-9]*"
                autoComplete="one-time-code"
                enterKeyHint="done"
                maxLength={6}
                value={code}
                onFocus={() => {
                  if (message && !verified) setMessage("");
                }}
                onChange={(e) => {
                  setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                  if (message && !verified) setMessage("");
                }}
                placeholder="000000"
                aria-label="Código de verificación de WhatsApp"
                className="kd9-input"
                style={{
                  display: "block",
                  width: "100%",
                  minWidth: 0,
                  minHeight: 58,
                  padding: "14px 16px",
                  boxSizing: "border-box",
                  textAlign: "center",
                  fontSize: 22,
                  fontWeight: 700,
                  letterSpacing: "0.3em",
                  lineHeight: 1.2,
                }}
                data-testid="whatsapp-otp-input"
              />
              <button
                type="button"
                disabled={busy || code.length !== 6}
                onClick={confirmCode}
                className="kd9-btn-primary rounded-lg text-[11px]"
                style={{ width: "100%", minHeight: 50, padding: "12px 18px" }}
                data-testid="whatsapp-otp-verify-btn"
              >
                {busy ? "…" : "Verificar"}
              </button>
            </div>
            <p className="text-[11px] font-mont" style={{ color: "var(--kd9-text-soft)" }}>
              Escribe los 6 dígitos recibidos por WhatsApp. El campo permanece visible y editable en móvil.
            </p>
          </div>
        </>
      )}

      {message && (
        <p className="text-xs font-mont" style={{ color: verified ? "#86EFAC" : "var(--kd9-text-soft)" }}>
          {message}
        </p>
      )}
    </div>
  );
}
